import { useApolloClient } from '@apollo/client';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import {
  UserPresence,
  UserPresenceStatus,
} from 'src/graphql/entities/user-presence/user-presence';
import { requestPresence } from 'src/graphql/entities/user-presence/user-presence-fetcher';
import { isStalePresence, usePresenceStore } from 'src/store/user-presence-store';
import { shallow } from 'zustand/shallow';
import { useUserPresenceSubscriptionToStore } from './use-user-presence-subscription';

// an "inactive" WS ping can be lost (PostToConnection rate limits, dropped
// connections), so cached "active" entries must expire on their own
const SWEEP_INTERVAL_MS = 30_000;

const RENDER_GRACE_SEC = 45;

const nowSec = () => Math.floor(Date.now() / 1000);

export const useUsersPresenceCached = (unstableUserIds: string[]) => {
  const { user } = useCurrentUserProfile();
  const client = useApolloClient();

  useUserPresenceSubscriptionToStore();

  const userIdsKey = Array.from(new Set(unstableUserIds)).sort().join(',');

  const normalizedUserIds = useMemo(
    () => (userIdsKey ? userIdsKey.split(',').filter(Boolean) : []),
    [userIdsKey],
  );

  const slice = usePresenceStore(
    useMemo(
      () => (s) => normalizedUserIds.map((id) => s.byId[id] as UserPresence | undefined),
      // using here and below a stable stringified dependency to not rely on incoming array stability
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [userIdsKey],
    ),
    // avoid re-renders when selected references did not change
    shallow,
  );

  const [now, setNow] = useState(nowSec);

  const userPresenceMap = useMemo(() => {
    const m = new Map<string, UserPresence>();

    if (user?.userId) {
      const tz = dayjs.tz?.guess?.() ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

      m.set(user.userId, {
        userId: user.userId,
        status: UserPresenceStatus.active,
        tz,
        ts: 0,
        assigneeGroups:
          user.conversationAssigneeGroups?.map(({ id, name }) => ({ id, name })) ?? [],
        lastSeen: 0,
        expiresAt: 0,
        updatedAt: 0,
      });
    }

    normalizedUserIds.forEach((id, i) => {
      const viewValue = slice[i];

      if (!viewValue) return;

      const hasExpired =
        viewValue.status === UserPresenceStatus.active &&
        now > viewValue.expiresAt + RENDER_GRACE_SEC;

      m.set(
        id,
        hasExpired ? { ...viewValue, status: UserPresenceStatus.inactive } : viewValue,
      );
    });

    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slice, now, user?.userId, user?.conversationAssigneeGroups]);

  const loading = usePresenceStore(
    useMemo(
      () => (s) => normalizedUserIds.some((id) => !!s.inFlight[id]),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [userIdsKey],
    ),
    Object.is,
  );

  useEffect(() => {
    if (!normalizedUserIds.length) return;
    requestPresence(normalizedUserIds, client);
    // we want to trigger this useEffect by specific keys only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, userIdsKey]);

  // self healing interval to remove expired active entries
  useEffect(() => {
    if (!normalizedUserIds.length) {
      return;
    }

    const timer = window.setInterval(() => {
      setNow(nowSec());

      const { byId } = usePresenceStore.getState();

      const expiredActive = normalizedUserIds.filter((id) => {
        const p = byId[id];

        return p?.status === UserPresenceStatus.active && isStalePresence(p);
      });
      if (expiredActive.length) requestPresence(expiredActive, client);
    }, SWEEP_INTERVAL_MS);

    return () => window.clearInterval(timer);
    // we want to trigger this useEffect by specific keys only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, userIdsKey]);

  return { userPresenceMap, loading };
};
